import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Search, HelpCircle, ArrowRight } from "lucide-react";

const faqGroups = [
  {
    category: "General",
    questions: [
      {
        question: "What does Square Trigger do?",
        answer: (
          <>
            Square Trigger is a digital agency in Vancouver offering{" "}
            <Link
              to="/services/website-development"
              className="text-primary hover:underline"
            >
              website development
            </Link>
            ,{" "}
            <Link
              to="/services/seo-services"
              className="text-primary hover:underline"
            >
              SEO services
            </Link>
            ,{" "}
            <Link
              to="/services/social-media-marketing"
              className="text-primary hover:underline"
            >
              social media marketing
            </Link>
            ,{" "}
            <Link
              to="/services/custom-tool-creation"
              className="text-primary hover:underline"
            >
              custom tool creation
            </Link>
            , and ongoing{" "}
            <Link
              to="/services/maintenance-support"
              className="text-primary hover:underline"
            >
              maintenance &amp; support
            </Link>
            .
          </>
        ),
        schemaAnswer:
          "Square Trigger is a digital agency in Vancouver offering website development, SEO services, social media marketing, custom tool creation, and ongoing maintenance & support.",
      },
      {
        question: "Do you only work with businesses in Vancouver?",
        answer: (
          <>
            No. We are based in Vancouver, but we work with businesses across
            Canada and support clients remotely.
          </>
        ),
        schemaAnswer:
          "No. We are based in Vancouver, but we work with businesses across Canada and support clients remotely.",
      },
      {
        question: "What industries do you work with?",
        answer: (
          <>
            We have strong experience in insurance and automotive, and we also
            work with businesses that need better websites, stronger visibility,
            clearer messaging, or smarter digital systems.
          </>
        ),
        schemaAnswer:
          "We have strong experience in insurance and automotive, and we also work with businesses that need better websites, stronger visibility, clearer messaging, or smarter digital systems.",
      },
    ],
  },
  {
    category: "Services",
    questions: [
      {
        question: "Can I hire you for just one service?",
        answer: (
          <>
            Yes. Some clients need only one service, while others need a
            combination of{" "}
            <Link
              to="/services/website-development"
              className="text-primary hover:underline"
            >
              website development
            </Link>
            ,{" "}
            <Link
              to="/services/seo-services"
              className="text-primary hover:underline"
            >
              SEO
            </Link>
            ,{" "}
            <Link
              to="/services/social-media-marketing"
              className="text-primary hover:underline"
            >
              social media marketing
            </Link>
            ,{" "}
            <Link
              to="/services/custom-tool-creation"
              className="text-primary hover:underline"
            >
              custom tools
            </Link>
            , or{" "}
            <Link
              to="/services/maintenance-support"
              className="text-primary hover:underline"
            >
              support
            </Link>
            .
          </>
        ),
        schemaAnswer:
          "Yes. Some clients need only one service, while others need a combination of website development, SEO, social media marketing, custom tools, or support.",
      },
      {
        question: "Do you build custom websites?",
        answer: (
          <>
            Yes. We build websites that are designed around your business goals,
            user flow, and growth needs.
          </>
        ),
        schemaAnswer:
          "Yes. We build websites that are designed around your business goals, user flow, and growth needs.",
      },
      {
        question: "Do you offer SEO services for existing websites?",
        answer: (
          <>
            Yes. We can improve{" "}
            <Link
              to="/services/seo-services"
              className="text-primary hover:underline"
            >
              SEO services
            </Link>{" "}
            on an existing website by working on structure, content, internal
            linking, and technical issues.
          </>
        ),
        schemaAnswer:
          "Yes. We can improve SEO on an existing website by working on structure, content, internal linking, and technical issues.",
      },
      {
        question: "Do you create custom tools or dashboards?",
        answer: (
          <>
            Yes. We build custom web applications,{" "}
            <Link
              to="/services/custom-tool-creation"
              className="text-primary hover:underline"
            >
              dashboards
            </Link>
            ,{" "}
            <Link
              to="/services/custom-tool-creation"
              className="text-primary hover:underline"
            >
              calculators
            </Link>
            , and workflow-based digital tools for specific business needs.
          </>
        ),
        schemaAnswer:
          "Yes. We build custom web applications, dashboards, calculators, and workflow-based digital tools for specific business needs.",
      },
      {
        question: "Do you offer website maintenance after launch?",
        answer: (
          <>
            Yes. We provide ongoing{" "}
            <Link
              to="/services/maintenance-support"
              className="text-primary hover:underline"
            >
              maintenance and support
            </Link>{" "}
            to help keep websites updated, secure, and working properly.
          </>
        ),
        schemaAnswer:
          "Yes. We provide ongoing maintenance and support to help keep websites updated, secure, and working properly.",
      },
    ],
  },
  {
    category: "Process",
    questions: [
      {
        question: "How do you start a project?",
        answer: (
          <>
            We start by understanding the business, the problem, and what needs
            to improve before recommending the right solution.
          </>
        ),
        schemaAnswer:
          "We start by understanding the business, the problem, and what needs to improve before recommending the right solution.",
      },
      {
        question: "How long does a project take?",
        answer: (
          <>
            It depends on the service and scope. Smaller projects move faster,
            while larger websites or custom builds take more time. We provide a
            clearer timeline after the discovery stage.
          </>
        ),
        schemaAnswer:
          "It depends on the service and scope. Smaller projects move faster, while larger websites or custom builds take more time. We provide a clearer timeline after the discovery stage.",
      },
      {
        question: "Do you use templates?",
        answer: (
          <>
            No. We build around what the business actually needs rather than
            forcing a one-size-fits-all package.
          </>
        ),
        schemaAnswer:
          "No. We build around what the business actually needs rather than forcing a one-size-fits-all package.",
      },
    ],
  },
  {
    category: "Contact & Working Together",
    questions: [
      {
        question: "How do I get started?",
        answer: (
          <>
            You can contact us through the form on the{" "}
            <Link to="/contact" className="text-primary hover:underline">
              Contact page
            </Link>{" "}
            and share what you need. We&apos;ll review it and guide you to the
            right next step.
          </>
        ),
        schemaAnswer:
          "You can contact us through the form on the Contact page and share what you need. We’ll review it and guide you to the right next step.",
      },
      {
        question: "Do you offer consultations?",
        answer: (
          <>
            Yes. You can reach out through the{" "}
            <Link to="/contact" className="text-primary hover:underline">
              Contact page
            </Link>{" "}
            to discuss your business needs and the best starting point.
          </>
        ),
        schemaAnswer:
          "Yes. You can reach out through the Contact page to discuss your business needs and the best starting point.",
      },
      {
        question: "Do you work remotely?",
        answer: (
          <>
            Yes. We are a remote-first agency based in Vancouver and support
            clients through online communication and digital collaboration.
          </>
        ),
        schemaAnswer:
          "Yes. We are a remote-first agency based in Vancouver and support clients through online communication and digital collaboration.",
      },
    ],
  },
];

export default function FAQsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const [email, setEmail] = useState("");

  const filteredFAQs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return faqGroups.flatMap((group) =>
      group.questions
        .filter((item) => {
          if (!query) return true;
          return (
            item.question.toLowerCase().includes(query) ||
            item.schemaAnswer.toLowerCase().includes(query) ||
            group.category.toLowerCase().includes(query)
          );
        })
        .map((item) => ({
          ...item,
          category: group.category,
        }))
    );
  }, [searchQuery]);

  return (
    <Layout>
      <SEO
        title="Digital Agency FAQs: Answers to Top Questions | Square Trigger"
        description="Find answers to the most Frequently Asked Questions about our Digital Agency's complete services, pricing, and more. Read more."
        keywords={SEO_KEYWORDS.faqs}
      />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
        <div className="absolute inset-0">
          <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
              <HelpCircle className="h-4 w-4 mr-2" />
              FAQs
            </Badge>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-6 leading-tight">
              Frequently Asked Questions
            </h1>

            <p className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              Find quick answers about our services, process, timelines, and how
              Square Trigger works with businesses.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center mb-8">
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/services/website-development">View Our Services</Link>
              </Button>
            </div>

            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-background/50" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              What Businesses Usually Ask First
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4">
            {filteredFAQs.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-xl border border-border">
                <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No matching questions found.
                </p>
              </div>
            ) : (
              filteredFAQs.map((faq, index) => (
                <motion.div
                  key={`${faq.category}-${faq.question}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.04 }}
                  className="relative z-10 bg-card rounded-xl border border-border overflow-hidden"
                >
                  <button
                    type="button"
                    onPointerUp={() =>
                      setOpenIndex(openIndex === index ? null : index)
                    }
                    className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors pointer-events-auto touch-manipulation"
                  >
                    <div className="pr-8">
                      <Badge variant="outline" className="mb-2 text-xs">
                        {faq.category}
                      </Badge>
                      <span className="font-semibold text-foreground block">
                        {faq.question}
                      </span>
                    </div>

                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                        openIndex === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openIndex === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqGroups.flatMap((group) =>
                group.questions.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.schemaAnswer,
                  },
                }))
              ),
            })}
          </script>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4 sm:mb-6">
              Still Have Questions?
            </h2>

            <p className="text-base sm:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Tell us what your business needs and we&apos;ll help you figure
              out the best next step.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="premium"
                size="lg"
                asChild
                className="bg-background text-foreground hover:bg-background/90 w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Contact Us
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">Book a Free Consultation</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-12 sm:pb-14 lg:pb-16 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center bg-card rounded-2xl border border-border shadow-soft p-6 sm:p-8 lg:p-10"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              Stay Updated With Digital Insights
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
              Get useful updates on website development, SEO, digital strategy,
              and business growth.
            </p>

            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
              />
              <Button
                type="submit"
                variant="gradient"
                size="lg"
                className="sm:w-auto w-full"
              >
                Subscribe
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}